<template>
	<div v-if="(isUserAllowedInLevel5or6 && isAuthenticatedUserInGroupAdmin) || isUserAllowedInLevel1">
		<BaseHeader :title="getTitle" />
		<div class="main">
			<div class="">
				<div>
					<div class="flex gap-6">
						<BaseSelect
							ref="filterBaseSelect"
							v-model="selectedFilter"
							:multiple="false"
							:options="filterOptions"
							:filter-only="true"
							class="w-1/3"
							:label="$t('filter')"
							@changed="getStatisticsForFilter"
						/>
						<BaseSelect
							ref="orderDocBaseSelect"
							v-if="selectedFilter?.value === 'all_order_documentation'"
							v-model="selectedOrderDocumentationType"
							:multiple="false"
							:options="orderDocumentationTypes"
							:filter-only="true"
							class="w-1/3"
							:label="$t('type')"
							@changed="getStatisticsForFilter"
						/>
						<BaseSelect
							ref="yearBaseSelect"
							v-model="years"
							v-if="viewType?.value !== 'pie'"
							:item-desktop-limit="5"
							:item-mobile-limit="5"
							:multiple="true"
							:options="yearsOptions"
							:filter-only="true"
							class="w-1/3"
							:label="$t('years')"
							@changed="getStatisticsForFilter"
						/>
						<div
							class="flex w-1/3 mt-10 ml-2 pr-3"
							v-if="
								(selectedFilter?.value === 'vehicles_active_since' || selectedFilter?.value === 'customers_active_since') && viewType?.value === 'pie'
							"
						>
							<div class="mt-1 w-full">
								<vue-slider
									v-model="sliderValue"
									:min="1"
									:max="24"
									:marks="sliderMarks"
									:lazy="true"
									:adsorb="true"
									@change="getStatisticsForFilterAsCake"
									:dot-options="dotOptions"
									:tooltip-placement="['top']"
								/>
							</div>
							<div class="font-bold pl-4">
								{{ $t('months') }}
							</div>
						</div>
					</div>
					<div class="flex justify-end">
						<BaseSelect
							ref="viewTypeBaseSelect"
							v-model="viewType"
							:filter-only="true"
							:options="viewTypesOptions"
							class="w-1/4"
							:label="$t('view')"
							@input="checkViewType"
						/>
					</div>
				</div>
			</div>
			<div class="mt-24">
				<BaseChart
					v-show="viewType && (viewType.value === 'chart' || viewType.value === 'pie')"
					:datasets="datasets"
					:labels="getLabels()"
					class="h-128"
					:type="viewType?.value"
				/>

				<div v-if="statistics" v-show="viewType && viewType.value === 'table'" class="space-y-32">
					<div v-for="table in getTables" :key="table.display">
						<div class="text-left text-xl mb-8">{{ selectedFilter.display }}</div>
						<div class="shadow sm:rounded-lg">
							<div class="flex w-full divide-x">
								<div class="w-full p-2 truncate">{{ $t('year') }}</div>
								<div v-for="month in months" :key="month" class="w-full p-2 truncate">
									{{ month }}
								</div>
							</div>

							<div v-for="yearIndex in getTableData(table.value)" :key="yearIndex" class="flex w-full divide-x border-t odd:bg-gray-200">
								<div class="font-bold w-full p-2">{{ yearIndex }}</div>
								<div
									v-for="(statisticsMonth, monthIndex) in table.value ? statistics[table.value][yearIndex] : statistics[yearIndex]"
									:key="monthIndex"
									:title="months[monthIndex - 1] + ' ' + yearIndex"
									class="w-full p-2"
								>
									{{ statisticsMonth }}
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
			<Footer v-if="isUserAllowedInLevel1" @backButtonClick="$router.go(-1)" :is-continue-button="false" :is-skippable="false" :is-back-button="true">
			</Footer>
		</div>
	</div>
	<Forbidden v-else />
</template>

<script>
import BaseHeader from '@/components/navigation/BaseHeader';
import BaseSelect from '@/components/forms/inputs/BaseSelect';
import { getOptionFromOptionsArray } from '@/plugins/Utils';
import BaseChart from '@/components/charts/BaseChart';
import Authentication from '@/mixins/Authentication';
import Forbidden from '@/views/default/Forbidden.vue';
import Footer from '@/components/damagecases/Footer.vue';
import StatisticsRepository from '@/api/repositories/StatisticsRepository';
import VueSlider from 'vue-slider-component';
import 'vue-slider-component/theme/default.css';
import OrderDocumentationTypesRepository from '@/api/repositories/OrderDocumentationTypesRepository';

export default {
	name: 'Statistics',
	components: { Forbidden, BaseChart, BaseSelect, BaseHeader, Footer, VueSlider },
	mixins: [Authentication],
	data() {
		return {
			years: [],
			viewType: null,
			themeOption: null,
			company: null,
			statistics: null,
			datasets: null,
			filterOptions: [
				{ display: this.$t('new_customers'), value: 'customers_created_since' },
				{ display: this.$t('new_vehicles'), value: 'vehicles_created_since' },
				{ display: this.$t('total_vehicles'), value: 'vehicles' },
				{ display: this.$t('total_customers'), value: 'customers' },
				{ display: this.$t('active_vehicles'), value: 'vehicles_active_since' },
				{ display: this.$t('active_customers'), value: 'customers_active_since' },
				{ display: this.$t('all_processes'), value: 'damage_cases' },
				{ display: this.$t('all_liability'), value: 'all_liability' },
				{ display: this.$t('all_comprehensive'), value: 'all_comprehensive' },
				{ display: this.$t('all_order_documentations'), value: 'all_order_documentation' },
			],
			orderDocumentationTypes: [],
			selectedOrderDocumentationType: null,
			sliderMarks: {},
			selectedFilter: null,
			sliderValue: [1],
			dotOptions: [{ tooltip: 'always' }],
			pieLabels: [],
		};
	},
	mounted() {
		if ((this.isUserAllowedInLevel5or6 && this.isAuthenticatedUserInGroupAdmin) || this.isUserAllowedInLevel1) {
			this.years = [getOptionFromOptionsArray(this.yearsOptions, this.currentYear)];
			this.viewType = this.viewTypesOptions[0];
			this.sliderMarks[1] = 1;
			this.sliderMarks[24] = 24;
			this.selectedFilter = this.filterOptions[0];
			this.getStatisticsForFilter();
			this.fetchOrderDocumentationTypes();
		}
	},
	computed: {
		getTables() {
			return [{ display: this.$t('data'), value: null }];
		},
		getTitle() {
			if (this.$route.params.company_id) {
				return this.$t('statistics') + ' - ' + this.company?.name;
			}
			return this.$t('statistics');
		},
		months() {
			return ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
		},
		currentYear() {
			return new Date().getFullYear();
		},
		yearsOptions() {
			const list = [];
			const year = this.currentYear;
			for (let i = year; i > year - 10; i--) {
				list.push({
					display: i,
					value: i,
				});
			}
			return list;
		},
		viewTypesOptions() {
			const list = [];
			list.push({ display: 'Grafik', value: 'chart' });
			list.push({ display: 'Tabelle', value: 'table' });
			if (this.selectedFilter?.value === 'vehicles_active_since' || this.selectedFilter?.value === 'customers_active_since') {
				list.push({ display: 'Kuchen', value: 'pie' });
			}
			return list;
		},
	},
	methods: {
		getLabels() {
			if (this.viewType?.value === 'pie') {
				return this.pieLabels;
			} else {
				return this.months;
			}
		},
		checkViewType(event) {
			if (event.value === 'pie') {
				this.getStatisticsForFilterAsCake();
			}
		},
		fetchOrderDocumentationTypes() {
			new OrderDocumentationTypesRepository()
				.fetchAllOrderDocumentationTypes()
				.then(response => {
					let rawTypes = [];
					if (this.isUserAllowedInLevel1) {
						rawTypes = [...response.data.ownTypes].sort((a, b) => a.name.localeCompare(b.name));
					} else {
						rawTypes = [...response.data.adminTypes, ...response.data.ownTypes].sort((a, b) => a.name.localeCompare(b.name));
					}

					rawTypes.forEach(element => {
						this.orderDocumentationTypes.push({ display: element.name, value: element.id });
					});
					this.orderDocumentationTypes.unshift({ display: 'Alle', value: 'all' });
					this.selectedOrderDocumentationType = this.orderDocumentationTypes[0];
				})
				.catch(e => {
					console.log(e);
				});
		},
		getStatisticsForFilterAsCake() {
			new StatisticsRepository()
				.getForFilter(
					this.sliderValue,
					'2025-01-01',
					{
						filters: [{ key: this.selectedFilter.value }],
						months: this.sliderValue,
					},
					this.isUserAllowedInLevel1 ? this.$route.params.company_id : null,
				)
				.then(response => {
					this.pieLabels = response.data.map(element => element.label);
					const data = response.data.map(element => element.count);
					this.datasets = [{ label: 'Dataset 1', backgroundColor: ['rgb(255, 99, 132)', 'rgb(54, 162, 235)'], data: data }];
				})
				.catch(e => {});
		},
		getStatisticsForFilter() {
			if (
				this.selectedFilter?.value !== 'vehicles_active_since' &&
				this.selectedFilter?.value !== 'customers_active_since' &&
				this.viewType.value === 'pie'
			) {
				this.viewType = this.viewTypesOptions[0];
			}
			if (this.viewType.value === 'pie') {
				return this.getStatisticsForFilterAsCake();
			}
			let request = {};
			request = { filter_key: this.selectedFilter.value, years: this.years.map(year => year.value) };
			if (this.selectedFilter.value === 'all_liability') {
				request.filter_key = 'damage_cases_with_process_type';
				request.process_type = 'liability';
			} else if (this.selectedFilter.value === 'all_comprehensive') {
				request.filter_key = 'damage_cases_with_process_type';
				request.process_type = 'comprehensive';
			} else if (this.selectedFilter.value === 'all_order_documentation') {
				request.filter_key = 'damage_cases_with_order_doc_type';
				request.process_type = 'order_documentation';
				if (this.selectedOrderDocumentationType.value !== 'all') {
					request.order_documentation_type = this.selectedOrderDocumentationType.value;
				}
			}

			if (this.isUserAllowedInLevel1) {
				request.company_id = this.$route.params.company_id;
			}

			new StatisticsRepository()
				.showStatistics(request)
				.then(response => {
					if (this.isUserAllowedInLevel1) {
						this.company = response.data.company;
					}
					this.statistics = response.data.statistics;
					this.datasets = this.getYearsDataset(this.statistics, false, this.selectedFilter.display);
				})
				.catch(e => {
					console.log(e);
				});
		},
		getTableData(key) {
			if (!this.statistics) return [];
			if (key) {
				if (!this.statistics[key]) return [];
				return Object.keys(this.statistics[key]).reverse();
			}
			return Object.keys(this.statistics).reverse();
		},
		getYearsDataset(statistics, calcAVG, label = '') {
			const datasets = [];

			this.years.forEach(year => {
				let yearAVG = 0;

				const data = this.months.map((month, index) => {
					const y = statistics && statistics[year.value] ? statistics[year.value][index + 1] : 0;
					yearAVG += y;

					return {
						x: month,
						y: y,
					};
				});

				datasets.push({
					label: label + ' ' + year.display,
					data: data,
				});

				if (calcAVG) {
					yearAVG = yearAVG / 12;
					datasets.push({
						type: 'line',
						label: label + this.$t('yearly_average') + ' ' + year.value,
						data: this.months.map(month => yearAVG),
						meta: {
							ignoreForStatistics: true,
						},
					});
				}
			});

			if (calcAVG && this.years.length > 1) {
				let monthValues = {};
				datasets.forEach(set => {
					if (!set.meta?.ignoreForStatistics) {
						set.data.forEach(d => {
							if (!d.meta?.ignoreForStatistics) {
								monthValues[d.x] = monthValues[d.x] ? monthValues[d.x] + d.y : d.y;
							}
						});
					}
				});

				monthValues = Object.values(monthValues).map(month => month / this.years.length);
				const completeAvg = monthValues.reduce((a, b) => a + b) / 12;
				const compleAVGValues = monthValues.map(month => completeAvg);

				datasets.push({
					type: 'line',
					label: label + 'Monatsdurchschnitt',
					data: monthValues,
					hidden: true,
				});

				datasets.push({
					type: 'line',
					label: label + this.$t('total_average'),
					data: compleAVGValues,
					hidden: true,
				});
			}

			return datasets;
		},
	},
};
</script>

<style scoped>
.truncate {
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}
</style>
